import React, { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { faEuroSign } from '@fortawesome/free-solid-svg-icons';
import CgInfoHint from 'modules/Common/ui/CgInfoHint';
import ControlWithTooltip from 'modules/Analytics/components/ControlWithTooltip';
import { useCalculateStore } from 'modules/Analytics/stores/useCalculateStore';
import {
  buildCertificatePricePresets,
  PRICE_QUERY_PARAM,
  clampCertificatePrice,
  persistCertificatePrice,
} from '../utils/certificatePrice';

/**
 * Viewer-controlled cost assumption strip above the products table.
 *
 * The certificate price is the one figure on this page the *declarant* owns —
 * everything else is the operator's published data. It's edited through the
 * same ControlWithTooltip used across the app (hover dropdown of suggested
 * prices) and written to useCalculateStore.carbonPrice, so the table's CBAM
 * Cost column, the estimator, and each row's breakdown popover recalculate
 * live. The value is mirrored to the URL (?price=) + localStorage so a
 * refreshed / shared link keeps the chosen figure.
 *
 * `quarterPrices` are the EEX reference averages, fetched by the parent page
 * (public gate on the share link, authenticated endpoint in the operator's own
 * Studio preview — see `hooks/usePortalCertificatePrices`). Passed in rather
 * than fetched here so this component stays presentational and the two pages
 * can each use the endpoint they are entitled to.
 *
 * With none supplied the dropdown still offers the lower/upper bounds, so a
 * failed or still-in-flight price call degrades to a shorter list, never to a
 * broken control.
 */
export default function CatalogAssumptionsBar({ quarterPrices }) {
  const { carbonPrice, setCarbonPrice } = useCalculateStore();
  const history = useHistory();
  const location = useLocation();

  const priceOptions = useMemo(() => buildCertificatePricePresets(quarterPrices), [quarterPrices]);

  const handlePriceChange = (raw) => {
    // Keep the field responsive while typing (mirrors the app-wide control),
    // and only persist / sync the URL once the value is valid.
    setCarbonPrice(raw);
    const n = clampCertificatePrice(raw);
    if (n == null) return;
    persistCertificatePrice(n);
    const params = new URLSearchParams(location.search);
    params.set(PRICE_QUERY_PARAM, String(n));
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  return (
    <div
      id="cost-assumptions"
      className="mb-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 flex flex-wrap items-center justify-between gap-3 scroll-mt-4"
    >
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Your cost assumption
        </span>
        <CgInfoHint
          label="Your cost assumption"
          position="top"
          maxWidth="max-w-[280px]"
        >
          <p className="mb-0">
            This is <span className="font-semibold text-gray-700">your</span> estimate for the CBAM
            cost calculation — not a value set by the supplier. Adjust it to the EU carbon /
            certificate price you expect to pay. Your choice is kept in the link so you can share or
            revisit it.
          </p>
        </CgInfoHint>
      </div>

      <ControlWithTooltip
        label="Certificate price"
        value={carbonPrice}
        onChange={handlePriceChange}
        icon={faEuroSign}
        unit="/tCO₂e"
        placeholder="75.36"
        tooltipTitle="Suggested certificate prices"
        tooltipOptions={priceOptions}
        inputWidth="w-16"
      />
    </div>
  );
}
